/*----------------------------------------------------------------------------*\
  #WEATHER
\*----------------------------------------------------------------------------*/

import { createEl } from 'util/create-el';

const OPEN_METEO_FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';

// Our weather icon assets (app/assets/images/weather-icons) are keyed by
// WeatherAPI.com condition codes, so pull them in for lookup by that code.
const dayIcons = require.context('images/weather-icons/day', false, /\.svg$/);
const nightIcons = require.context('images/weather-icons/night', false, /\.svg$/);

// Maps Open-Meteo's WMO weather codes to the closest WeatherAPI.com
// condition code, so we can reuse our existing weather icon assets.
// https://open-meteo.com/en/docs#weathervariables
const WMO_TO_ICON_CODE = {
  0: 113, // Clear
  1: 113, // Mainly clear
  2: 116, // Partly cloudy
  3: 122, // Overcast
  45: 248, // Fog
  48: 260, // Freezing fog
  51: 263, // Light drizzle
  53: 266, // Drizzle
  55: 302, // Dense drizzle
  56: 281, // Freezing drizzle
  57: 284, // Heavy freezing drizzle
  61: 293, // Slight rain
  63: 302, // Rain
  65: 308, // Heavy rain
  66: 311, // Light freezing rain
  67: 314, // Heavy freezing rain
  71: 323, // Slight snow
  73: 332, // Snow
  75: 338, // Heavy snow
  77: 350, // Snow grains
  80: 353, // Slight rain showers
  81: 356, // Rain showers
  82: 359, // Violent rain showers
  85: 368, // Slight snow showers
  86: 371, // Snow showers
  95: 200, // Thunderstorm
  96: 386, // Thunderstorm with hail
  99: 389, // Thunderstorm with heavy hail
};

// Maps Open-Meteo's WMO weather codes to a human-readable description
// https://open-meteo.com/en/docs#weathervariables
const WEATHER_CODE_DESCRIPTIONS = {
  0: 'Clear',
  1: 'Mainly Clear',
  2: 'Partly Cloudy',
  3: 'Overcast',
  45: 'Fog',
  48: 'Freezing Fog',
  51: 'Light Drizzle',
  53: 'Drizzle',
  55: 'Heavy Drizzle',
  56: 'Light Freezing Drizzle',
  57: 'Freezing Drizzle',
  61: 'Light Rain',
  63: 'Rain',
  65: 'Heavy Rain',
  66: 'Light Freezing Rain',
  67: 'Freezing Rain',
  71: 'Light Snow',
  73: 'Snow',
  75: 'Heavy Snow',
  77: 'Snow Grains',
  80: 'Light Rain Showers',
  81: 'Rain Showers',
  82: 'Violent Rain Showers',
  85: 'Light Snow Showers',
  86: 'Snow Showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with Hail',
  99: 'Thunderstorm with Heavy Hail',
};

const describeWeatherCode = code => WEATHER_CODE_DESCRIPTIONS[code] || '';

// Look up the icon asset for a given weather code, falling back to null
// if we don't have a mapping or matching file for it.
const weatherIconUrl = (code, isDay) => {
  const iconCode = WMO_TO_ICON_CODE[code];
  if (!iconCode) return null;

  const icons = isDay ? dayIcons : nightIcons;
  const iconModule = icons(`./${iconCode}.svg`);

  return iconModule.default || iconModule;
};

const setWeather = $weather => {
  const { longitude, latitude } = $weather.dataset;
  if (!longitude || !latitude) return null;

  const url = `${OPEN_METEO_FORECAST_URL}?latitude=${latitude}&longitude=${longitude}&current_weather=true`;

  fetch(url)
    .then(response => response.json())
    .then(({ current_weather: currentWeather }) => {
      if (!currentWeather) return;

      const temperature = Math.round(currentWeather.temperature);
      const description = describeWeatherCode(currentWeather.weathercode);
      const iconUrl = weatherIconUrl(
        currentWeather.weathercode,
        currentWeather.is_day
      );

      if (iconUrl) {
        const $icon = createEl('img', {
          className: 'c-pageHeader__weatherIcon',
          src: iconUrl,
        });
        $weather.appendChild($icon);
      }

      const $text = createEl('span', {
        className: 'c-pageHeader__weatherText',
        innerHTML: `<b>${temperature}</b>°<br>${description}`.trim(),
      });
      $weather.appendChild($text);
    })
    .catch(() => {
      // Fail silently - weather is a non-critical enhancement for now
    });
};

const Weather = () => {
  // Public: Destroy module instance
  const destroy = () => {
    const $weather = document.querySelector('.c-pageHeader__weather');
    if (!$weather) return;

    const $icon = $weather.querySelector('.c-pageHeader__weatherIcon');
    const $text = $weather.querySelector('.c-pageHeader__weatherText');

    if ($icon) $icon.remove();
    if ($text) $text.remove();
  };

  // Public: Destroy module instance and run initialise again
  const reinit = () => {
    destroy();
    init();
  };

  // Public: Initialise module
  const init = () => {
    const $weather = document.querySelector('.c-pageHeader__weather');
    if (!$weather) return null;

    setWeather($weather);
  };

  // Return public methods
  return {
    destroy,
    reinit,
    init,
  };
};

// Default module export
export default Weather;
